import { MutableRefObject, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

/* Semantic body-block utilities for the standard article experience:
   tokenizer, hero-duplicate suppression safety net, Expand Story preview
   boundary and the rail-aware wide-mode measurement hook. */

export type BodyItem =
  | { kind: "html"; html: string; media: boolean }
  | { kind: "ad"; html?: undefined; media?: undefined }
  | { kind: "chart"; html?: undefined; media?: undefined }
  | { kind: "wtm"; html?: undefined; media?: undefined };

const BLOCK_TAG_RE = /<(p|h[1-6]|ul|ol|blockquote|figure|table|pre|div|section|aside|picture|iframe|video)\b/gi;

export const tokenizeBlocks = (html: string): string[] => {
  const blocks: string[] = [];
  let i = 0;
  while (i < html.length) {
    BLOCK_TAG_RE.lastIndex = i;
    const m = BLOCK_TAG_RE.exec(html);
    if (!m) {
      const rest = html.slice(i).trim();
      if (rest) blocks.push(rest);
      break;
    }
    const before = html.slice(i, m.index).trim();
    if (before) blocks.push(before);
    const tag = m[1].toLowerCase();
    const tok = new RegExp(`<${tag}\\b|</${tag}\\s*>`, "gi");
    tok.lastIndex = m.index;
    let depth = 0;
    let end = html.length;
    let t: RegExpExecArray | null;
    while ((t = tok.exec(html))) {
      if (t[0].charAt(1) === "/") {
        depth -= 1;
        if (depth === 0) { end = tok.lastIndex; break; }
      } else {
        depth += 1;
      }
    }
    const block = html.slice(m.index, end).trim();
    if (block) blocks.push(block);
    i = end;
  }
  return blocks;
};

const CONTAINER_RE = /^<(div|section|article|main)\b[^>]*>([\s\S]*)<\/\1\s*>$/i;

/* Extraction output is often wrapped in container divs — flatten them so
   paragraphs become individually addressable semantic blocks. */
const flattenBlocks = (blocks: string[], depth = 0): string[] => {
  if (depth > 3) return blocks;
  const out: string[] = [];
  for (const b of blocks) {
    const m = b.match(CONTAINER_RE);
    if (m) {
      const inner = tokenizeBlocks(m[2].trim());
      if (inner.length >= 2 || (inner.length === 1 && inner[0] !== b)) {
        out.push(...flattenBlocks(inner, depth + 1));
        continue;
      }
    }
    out.push(b);
  }
  return out;
};

export const textLen = (html: string): number =>
  html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length;

export const isMediaBlock = (html: string): boolean => {
  const m = html.trim().match(/^<([a-z0-9]+)/i);
  const tag = m ? m[1].toLowerCase() : "";
  if (["figure", "table", "picture", "iframe", "video", "img", "pre"].includes(tag)) return true;
  if (tag === "div" || tag === "section" || tag === "aside")
    return /<(img|table|iframe|video)\b/i.test(html) && textLen(html) < 200;
  return false;
};

export const isHeadingBlock = (item: BodyItem): boolean =>
  item.kind === "html" && /^<h[1-6]\b/i.test(item.html.trim());

/* ---- hero duplicate suppression (render-time safety net; backend is primary) ---- */

const GENERIC_STEMS = new Set([
  "image", "img", "photo", "picture", "default", "logo", "banner", "placeholder",
  "thumbnail", "thumb", "icon", "header", "featured", "untitled", "download",
  "file", "unnamed", "screenshot", "cover", "hero", "avatar", "og", "og-image",
  "ogimage", "share", "opengraph", "article", "news", "post",
]);

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const hostOf = (u: string) => {
  try { return new URL(u, window.location.origin).host.replace(/^www\./, "").toLowerCase(); }
  catch { return ""; }
};
const pathOf = (u: string) => {
  try { return decodeURIComponent(new URL(u, window.location.origin).pathname).replace(/\/+$/, "").toLowerCase(); }
  catch { return ""; }
};
const stemOf = (u: string) => {
  const name = pathOf(u).split("/").pop() || "";
  const base = name.includes(".") ? name.slice(0, name.lastIndexOf(".")) : name;
  return base.replace(/(?:[-_](?:\d{2,4}x\d{2,4}|scaled|edited|copy|thumb|thumbnail|small|medium|large|full|\d{2,4}w))+$|@[23]x$/i, "");
};

const sameIdentity = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (hostOf(a) + pathOf(a) === hostOf(b) + pathOf(b)) return true;
  const sa = stemOf(a);
  return !!sa && sa === stemOf(b) && sa.length >= 8 && !GENERIC_STEMS.has(sa) && hostOf(a) === hostOf(b);
};

export const suppressHeroDuplicates = (html: string, heroUrl?: string | null): string => {
  let out = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const dims = Array.from(tag.matchAll(/\b(width|height)\s*=\s*["']?(\d+)/gi)).map((d) => parseInt(d[2], 10));
    return dims.length && Math.max(...dims) <= 3 ? "" : tag;
  });
  if (heroUrl) {
    const dupSrcs: string[] = [];
    out.replace(/<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi, (tag, src) => {
      if (sameIdentity(src, heroUrl)) dupSrcs.push(src);
      return tag;
    });
    for (const src of dupSrcs) {
      const fig = new RegExp(
        "<figure\\b[^>]*>(?:(?!<\\/figure>)[\\s\\S])*?" + escapeRe(src) + "(?:(?!<\\/figure>)[\\s\\S])*?<\\/figure>", "i");
      if (fig.test(out)) out = out.replace(fig, "");
      else out = out.replace(new RegExp("<img\\b[^>]*" + escapeRe(src) + "[^>]*>", "i"), "");
    }
  }
  return out.replace(/<figure\b[^>]*>\s*(?:<figcaption\b[^>]*>[\s\S]*?<\/figcaption>\s*)?<\/figure>/gi, "");
};

/* ---- item assembly ---- */

export const buildBodyItems = (
  body: string | null | undefined, heroUrl?: string | null, dropCap?: boolean,
): BodyItem[] => {
  if (!body) return [];
  let html = body.replace(/<a\b[^>]*>/gi, "").replace(/<\/a>/gi, "");
  html = suppressHeroDuplicates(html, heroUrl);
  if (dropCap) html = html.replace(/<p(>|\s)/, '<p class="nl-dropcap"$1');
  const blocks = flattenBlocks(tokenizeBlocks(html));
  const out: BodyItem[] = blocks.map((b) => ({ kind: "html", html: b, media: isMediaBlock(b) }));
  const n = out.length;
  if (n >= 8) {
    const cutAd = Math.min(Math.max(2, Math.round(n * 0.3)), n - 4);
    const cutData = Math.min(Math.max(cutAd + 2, Math.round(n * 0.62)), n - 2);
    out.splice(cutData, 0, { kind: "chart" }, { kind: "wtm" });
    out.splice(cutAd, 0, { kind: "ad" });
  } else {
    out.push({ kind: "wtm" });
  }
  return out;
};

/* ---- Expand Story boundary: ~12-18 visible lines, cut at a complete block ---- */

const PREVIEW_CHARS = 1200;   // ≈15 lines at ~80 chars/line in the 720px column
const MIN_TOTAL = 1700;       // don't collapse genuinely short articles
const MIN_HIDDEN = 450;       // collapsing must hide something meaningful (~6 lines)

export const computePreviewCount = (items: BodyItem[]): number => {
  const narrative = (it: BodyItem) => (it.kind === "html" && !it.media ? textLen(it.html) : 0);
  const total = items.reduce((s, it) => s + narrative(it), 0);
  if (total < MIN_TOTAL) return items.length;
  let chars = 0;
  let cut = items.length;
  for (let i = 0; i < items.length; i++) {
    chars += narrative(items[i]);
    if (chars >= PREVIEW_CHARS && items[i].kind === "html") { cut = i + 1; break; }
  }
  const hidden = items.slice(cut).reduce((s, it) => s + narrative(it), 0);
  if (hidden < MIN_HIDDEN) return items.length;
  return cut;
};

/* ---- Wide mode: measure the true rendered bottom of the right rail and find the
   first complete article block below it. Recalculates when the rail resizes
   (ads, related coverage, intelligence rows, premium cards), on viewport resize
   and on inline image loads. Measurement happens in an all-narrow layout pass
   committed before paint, so there is no visible flash. ---- */

export const useWideMode = (opts: {
  enabled: boolean;
  railRef: RefObject<HTMLDivElement>;
  itemRefs: MutableRefObject<(HTMLElement | null)[]>;
  tailRef: RefObject<HTMLDivElement>;
  itemCount: number;
  resetKey: string;
}): { wideFrom: number | null } => {
  const { enabled, railRef, itemRefs, tailRef, itemCount, resetKey } = opts;
  const [wideFrom, setWideFrom] = useState<number | null>(null);
  const [measuring, setMeasuring] = useState(true);
  const [tick, setTick] = useState(0);
  const measuringRef = useRef(measuring);
  measuringRef.current = measuring;
  const railBottomRef = useRef(-1);

  /* Phase 1 — request a measure: render everything narrow first */
  useLayoutEffect(() => {
    setMeasuring(true);
    setWideFrom(null);
  }, [enabled, itemCount, resetKey, tick]);

  /* Phase 2 — measure the all-narrow layout before paint, then commit the split */
  useLayoutEffect(() => {
    if (!measuring) return;
    if (!enabled || !railRef.current) {
      setWideFrom(null);
      setMeasuring(false);
      return;
    }
    const railBottom = railRef.current.getBoundingClientRect().bottom + window.scrollY;
    railBottomRef.current = railBottom;
    let idx: number | null = null;
    for (let i = 0; i < itemCount; i++) {
      const el = itemRefs.current[i];
      if (!el) continue;
      if (el.getBoundingClientRect().top + window.scrollY >= railBottom - 2) { idx = i; break; }
    }
    if (idx === null && tailRef.current
        && tailRef.current.getBoundingClientRect().top + window.scrollY >= railBottom - 2) {
      idx = itemCount;
    }
    setWideFrom(idx);
    setMeasuring(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuring, enabled, itemCount]);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const remeasure = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setTick((v) => v + 1), 160);
    };
    const ro = new ResizeObserver(() => {
      if (measuringRef.current) return;
      const rail = railRef.current;
      if (!rail) return;
      const rb = rail.getBoundingClientRect().bottom + window.scrollY;
      if (Math.abs(rb - railBottomRef.current) > 4) remeasure();
    });
    if (railRef.current) ro.observe(railRef.current);
    window.addEventListener("resize", remeasure);
    const onImgLoad = (e: Event) => {
      if ((e.target as HTMLElement | null)?.tagName === "IMG") remeasure();
    };
    document.addEventListener("load", onImgLoad, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", remeasure);
      document.removeEventListener("load", onImgLoad, true);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { wideFrom: measuring ? null : wideFrom };
};
